/**
 * International distributor request — component.
 * One `InternationalRequestFormBinding` per `form[data-international-request-form]`.
 * POST JSON to `/apps/public/international-request` (app proxy).
 *
 * @see window.__internationalRequestApiUrl, window.internationalRequestStrings
 * @class InternationalRequestFormComponent
 * @class InternationalRequestFormBinding
 */
const DEFAULT_API_URL = '/apps/public/international-request';

class InternationalRequestFormComponent {
  /**
   * @param {string} key
   * @param {string} fallback
   * @returns {string}
   */
  static str(key, fallback) {
    const S = window.internationalRequestStrings || {};
    return S[key] || fallback;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} id
   * @returns {string}
   */
  static fieldValue(form, id) {
    const el = form.querySelector('#' + (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id));
    return el ? String(el.value || '').trim() : '';
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   * @returns {string}
   */
  static hiddenValue(form, name) {
    const el = form.querySelector('input[name="' + name + '"]');
    return el ? String(el.value || '').trim() : '';
  }

  /**
   * @returns {string}
   */
  static resolveSubmittedFrom() {
    if (typeof globalThis.getActiveWatkinsCountryForDetectedContent === 'function') {
      try {
        return globalThis.getActiveWatkinsCountryForDetectedContent();
      } catch {
        /* */
      }
    }
    const g = window.DoublyGlobalCurrency;
    if (!g || g.currentCurrency == null || g.currentCurrency === '') {
      return '';
    }
    const c = g.currentCurrency;
    if (typeof c === 'string' || typeof c === 'number') {
      return String(c).trim();
    }
    if (typeof c === 'object') {
      const country =
        c.country ??
        c.countryName ??
        c.country_name ??
        c.currencyCountry ??
        c.name;
      if (country != null && country !== '') {
        return String(country).trim();
      }
    }
    return String(c).trim();
  }

  /**
   * @param {HTMLFormElement} form
   */
  static syncSubmittedFromField(form) {
    const input = form.querySelector('input[name="submitted_from"]');
    if (input) {
      input.value = InternationalRequestFormComponent.resolveSubmittedFrom();
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {string}
   */
  static customerPayloadValue(form) {
    const raw = InternationalRequestFormComponent.hiddenValue(form, 'customer');
    return raw === '' ? '-1' : raw;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {'error'|'success'} type
   * @param {string} message
   */
  static showFeedback(form, type, message) {
    const errEl = form.querySelector('[data-international-request-error]');
    const okEl = form.querySelector('[data-international-request-success]');
    if (errEl) {
      errEl.classList.toggle('d-none', type !== 'error');
      errEl.textContent = type === 'error' ? message : '';
    }
    if (okEl) {
      okEl.classList.toggle('d-none', type !== 'success');
      okEl.textContent = type === 'success' ? message : '';
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  static clearFeedback(form) {
    const errEl = form.querySelector('[data-international-request-error]');
    const okEl = form.querySelector('[data-international-request-success]');
    if (errEl) {
      errEl.classList.add('d-none');
      errEl.textContent = '';
    }
    if (okEl) {
      okEl.classList.add('d-none');
      okEl.textContent = '';
    }
  }

  static syncAllSubmittedFromFields() {
    document.querySelectorAll('form[data-international-request-form]').forEach(function (f) {
      InternationalRequestFormComponent.syncSubmittedFromField(f);
    });
  }

  static init() {
    if (window.__watkinsInternationalRequestLoaded) {
      return;
    }
    window.__watkinsInternationalRequestLoaded = true;

    const boot = function () {
      document.querySelectorAll('form[data-international-request-form]').forEach(function (form) {
        if (form.__watkinsInternationalRequestBound) {
          return;
        }
        form.__watkinsInternationalRequestBound = true;
        new InternationalRequestFormBinding(form).attach();
      });
      InternationalRequestFormComponent.syncAllSubmittedFromFields();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }

    window.addEventListener('load', InternationalRequestFormComponent.syncAllSubmittedFromFields);
  }
}

class InternationalRequestFormBinding {
  /**
   * @param {HTMLFormElement} form
   */
  constructor(form) {
    this.form = form;
    this._onSubmit = this.handleSubmit.bind(this);
  }

  buildPayload() {
    const form = this.form;
    const C = InternationalRequestFormComponent;
    return {
      companyName: C.fieldValue(form, 'companyName'),
      businessPhone: C.fieldValue(form, 'businessPhone'),
      contactPerson: C.fieldValue(form, 'contactPerson'),
      mobilePhone: C.fieldValue(form, 'mobilePhone'),
      email: C.fieldValue(form, 'emailAddress'),
      website: C.fieldValue(form, 'website'),
      fullAddress: C.fieldValue(form, 'fullAddress'),
      geographyToRepresent: C.fieldValue(form, 'geographyToRepresent'),
      productsToOffer: C.fieldValue(form, 'productsToOffer'),
      otherBrandsRepresent: C.fieldValue(form, 'otherBrandsRepresent'),
      businessReferences: C.fieldValue(form, 'businessReferences'),
      distributionChannelsOverview: C.fieldValue(form, 'distributionChannelsOverview'),
      brandBuildingApproach: C.fieldValue(form, 'brandBuildingApproach'),
      volumeExpectations3to5Years: C.fieldValue(form, 'volumeExpectations3to5Years'),
      preMarketAssessmentsRegulatory: C.fieldValue(form, 'preMarketAssessmentsRegulatory'),
      labelingRequirementsImport: C.fieldValue(form, 'labelingRequirementsImport'),
      relabelingPlans: C.fieldValue(form, 'relabelingPlans'),
      submitted_from: C.hiddenValue(form, 'submitted_from'),
      customer: C.customerPayloadValue(form),
    };
  }

  /**
   * @param {SubmitEvent} e
   */
  handleSubmit(e) {
    if (e.target !== this.form) {
      return;
    }
    const form = this.form;
    const C = InternationalRequestFormComponent;
    e.preventDefault();
    e.stopPropagation();
    C.clearFeedback(form);
    C.syncSubmittedFromField(form);

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const payload = this.buildPayload();
    const apiUrl = window.__internationalRequestApiUrl || DEFAULT_API_URL;

    const submitBtn = form.querySelector('button[type="submit"]');
    Utility.setSubmitButtonLoading(submitBtn, true);

    fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    })
      .then(function (r) {
        return r
          .json()
          .then(function (data) {
            return { ok: r.ok, status: r.status, data: data };
          })
          .catch(function () {
            return { ok: r.ok, status: r.status, data: {} };
          });
      })
      .then(function (result) {
        if (result.ok) {
          C.showFeedback(form, 'success', C.str('submitSuccess', 'Thank you. Your request was submitted.'));
          form.reset();
          C.syncSubmittedFromField(form);
        } else {
          const data = result.data;
          const msg =
            (data && (data.message || data.error || data.detail)) ||
            C.str('submitError', 'Submission could not be completed. Please try again.') +
              ' (' +
              result.status +
              ')';
          C.showFeedback(form, 'error', msg);
        }
      })
      .catch(function () {
        C.showFeedback(form, 'error', C.str('networkError', 'Network error. Please try again later.'));
      })
      .finally(function () {
        Utility.setSubmitButtonLoading(submitBtn, false);
      });
  }

  attach() {
    this.form.addEventListener('submit', this._onSubmit, true);
  }
}

globalThis.InternationalRequestFormComponent = InternationalRequestFormComponent;
InternationalRequestFormComponent.init();
